// redux/presence/presenceSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface PresenceLastSeenEntry {
  last_seen: string | null;
  last_seen_epoch: number | null;
  last_seen_display?: string | null;
}

interface PresenceState {
  onlineUsers: Record<number, boolean>;
  lastSeenByUser: Record<number, PresenceLastSeenEntry>;
}

const initialState: PresenceState = {
  onlineUsers: {},
  lastSeenByUser: {},
};

const presenceSlice = createSlice({
  name: "presence",
  initialState,
  reducers: {
    setUserOnlineStatus: (
      state,
      action: PayloadAction<{ userId: number; isOnline: boolean }>
    ) => {
      const { userId, isOnline } = action.payload;

      if (!Number.isFinite(userId) || userId <= 0) return;

      state.onlineUsers[userId] = isOnline;

      // Clear stale last_seen when user comes online
      if (isOnline && state.lastSeenByUser[userId]) {
        delete state.lastSeenByUser[userId];
      }
    },

    setUserLastSeen: (
      state,
      action: PayloadAction<{
        userId: number;
        last_seen: string | null;
        last_seen_epoch: number | null;
        last_seen_display?: string | null;
      }>
    ) => {
      const { userId, last_seen, last_seen_epoch, last_seen_display } = action.payload;

      if (!Number.isFinite(userId) || userId <= 0) return;

      state.onlineUsers[userId] = false;
      state.lastSeenByUser[userId] = {
        last_seen,
        last_seen_epoch,
        last_seen_display: last_seen_display ?? null,
      };
    },

    setPresenceSnapshot: (
      state,
      action: PayloadAction<Record<number, boolean>>
    ) => {
      const next: Record<number, boolean> = {};

      for (const [rawUserId, rawIsOnline] of Object.entries(action.payload)) {
        const userId = Number(rawUserId);
        if (!Number.isFinite(userId) || userId <= 0) continue;

        const isOnline = !!rawIsOnline;
        next[userId] = isOnline;

        if (isOnline && state.lastSeenByUser[userId]) {
          delete state.lastSeenByUser[userId];
        }
      }

      state.onlineUsers = {
        ...state.onlineUsers,
        ...next,
      };
    },

    clearPresence: (state) => {
      state.onlineUsers = {};
      state.lastSeenByUser = {};
    },
  },
});

export const {
  setUserOnlineStatus,
  setUserLastSeen,
  setPresenceSnapshot,
  clearPresence,
} = presenceSlice.actions;

export default presenceSlice.reducer;